// Phase 86AZ.60.1 — Public contractor candidates page (/muteahhit/adaylar)
// =========================================================================
// Anyone can browse this page to see who has been recommended as a
// contractor and by whom — a transparency feature. PII (contact email,
// phone, recommender email) is NOT exposed. Presentation files are
// downloadable inline.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  HardHat, Search, RefreshCw, FileText, Tag, User, Calendar,
  MessageSquare, ArrowLeft, Sparkles, Globe, LayoutGrid, List, Eye, X, Download, Maximize2, Mail, Phone,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import Navbar from '../components/Navbar';
import PresentationPreviewModal from '../components/PresentationPreviewModal';
import { useLanguage } from '../contexts/LanguageContext';

const API  = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = '#0A3A5A';

const PresentationButton = ({ candId, name, size, tr, onOpen }) => (
  <button type="button"
     onClick={() => onOpen({ candId, name, size })}
     className="inline-flex items-center gap-1.5 text-xs text-red-700 hover:text-red-800 hover:underline"
     data-testid={`public-aday-presentation-${candId}`}>
    <FileText className="h-3.5 w-3.5" />
    <span className="truncate max-w-[180px]">{name || (tr ? 'Sunum' : 'Presentation')}</span>
    {size != null && <span className="text-gray-400">({(size / 1024).toFixed(0)} KB)</span>}
  </button>
);

export default function MuteahhitAdaylariPublic() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [preview, setPreview] = useState(null);   // {candId, name, size} | null
  const [detail,  setDetail]  = useState(null);    // candidate row | null
  // Phase 86AZ.60.10 — Grid / List görünüm seçici (varsayılan grid)
  const [view,    setView]    = useState('grid');  // 'grid' | 'list'
  // Phase 86AZ.60.13 — Kategori filtresi ('' = Tümü)
  const [activeCategory, setActiveCategory] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contractor-recommenders/candidates`);
      setRows(r.data || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 86AZ.60.13 — Mevcut adaylardan benzersiz kategori listesi (sayım ile)
  const categories = (() => {
    const map = new Map();
    rows.forEach(r => {
      const c = (r.category || '').trim();
      if (!c) return;
      map.set(c, (map.get(c) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])               // çoktan aza
      .map(([name, count]) => ({ name, count }));
  })();

  const filtered = rows.filter(r => {
    if (activeCategory && (r.category || '').trim() !== activeCategory) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.company || '').toLowerCase().includes(q)
        || (r.category || '').toLowerCase().includes(q)
        || (r.recommender_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="bg-[#F7F5EF] min-h-screen notranslate" translate="no">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(60% 80% at 75% 20%, rgba(220,38,38,0.08) 0%, transparent 60%)`
        }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-28 pb-10">
          <Link to="/muteahhit" className="inline-flex items-center gap-1.5 text-xs text-[#5A6B7D] hover:text-red-700 mb-5">
            <ArrowLeft className="h-3 w-3" /> {tr ? 'Ana Sayfa' : 'Home'}
          </Link>
          <span className="inline-block text-[11px] uppercase tracking-[0.28em] font-semibold text-red-700 bg-red-50 border border-red-100 px-3 py-1 rounded-full mb-5">
            {tr ? 'Şeffaflık · Aday Havuzu' : 'Transparency · Candidate Pool'}
          </span>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-6" style={{ color: NAVY }}>
            {tr ? <>Önerilen <span className="italic text-red-700">tedarikçiler</span></> : <>Recommended <span className="italic text-red-700">suppliers</span></>}
          </h1>
          <p className="text-[15px] text-[#3A4A5C] leading-relaxed max-w-2xl">
            {tr
              ? 'COP 31 Antalya organizasyon komitesinin yetkilendirdiği önericiler tarafından önerilen tedarikçi firmalardır. Bu liste tamamen şeffaflık amacıyla yayınlanır; firmaların iletişim bilgileri gizliliği nedeniyle gösterilmez.'
              : 'These are supplier firms recommended by referrers authorized by the COP 31 Antalya organizing committee. This list is published purely for transparency; firms\' contact details are not shown to preserve privacy.'}
          </p>
        </div>
      </section>

      {/* ── STATS + SEARCH ── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Toplam Aday' : 'Total'}</p>
            <p className="font-playfair text-2xl mt-1" style={{ color: NAVY }}>{rows.length}</p>
          </div>
          <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Sunumlu' : 'With Presentation'}</p>
            <p className="font-playfair text-2xl text-red-600 mt-1">{rows.filter(r => r.has_presentation).length}</p>
          </div>
          <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Farklı Öneren' : 'Unique Referrers'}</p>
            <p className="font-playfair text-2xl text-emerald-600 mt-1">{new Set(rows.map(r => r.recommender_name).filter(Boolean)).size}</p>
          </div>
          <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">{tr ? 'Farklı Kategori' : 'Unique Categories'}</p>
            <p className="font-playfair text-2xl text-amber-600 mt-1">{new Set(rows.map(r => (r.category || '').toLowerCase()).filter(Boolean)).size}</p>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-2xl mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="public-aday-search"
                  placeholder={tr ? 'Firma, kategori veya öneren ara...' : 'Search by company, category or recommender...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 rounded-xl text-sm"
                />
              </div>
              {/* Görünüm seçici */}
              <div className="flex items-center bg-gray-100 rounded-xl p-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === 'grid' ? 'bg-white shadow-sm text-[#0A3A5A]' : 'text-gray-500 hover:text-gray-700'}`}
                  title={tr ? 'Izgara görünümü' : 'Grid view'}
                  data-testid="public-aday-view-grid">
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">{tr ? 'Izgara' : 'Grid'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === 'list' ? 'bg-white shadow-sm text-[#0A3A5A]' : 'text-gray-500 hover:text-gray-700'}`}
                  title={tr ? 'Liste görünümü' : 'List view'}
                  data-testid="public-aday-view-list">
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">{tr ? 'Liste' : 'List'}</span>
                </button>
              </div>
            </div>

            {/* Phase 86AZ.60.13 — Kategori filtre çipleri */}
            {categories.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-start gap-2">
                  <div className="hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-gray-500 font-semibold pt-1.5 flex-shrink-0">
                    <Tag className="h-3 w-3" /> {tr ? 'Kategori' : 'Category'}
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="public-aday-category-chips">
                    <button
                      type="button"
                      onClick={() => setActiveCategory('')}
                      data-testid="public-aday-category-all"
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                        activeCategory === ''
                          ? 'bg-[#0A3A5A] text-white border-[#0A3A5A] shadow-sm'
                          : 'bg-white text-[#0A3A5A] border-[rgba(10,58,90,0.15)] hover:border-[#0A3A5A]/40 hover:bg-[#0A3A5A]/5'
                      }`}>
                      {tr ? 'Tümü' : 'All'}
                      <span className={`text-[10px] tabular-nums ${activeCategory === '' ? 'text-white/80' : 'text-gray-400'}`}>{rows.length}</span>
                    </button>
                    {categories.map(({ name, count }) => {
                      const active = activeCategory === name;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setActiveCategory(active ? '' : name)}
                          data-testid={`public-aday-category-chip-${name}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                            active
                              ? 'bg-red-600 text-white border-red-600 shadow-sm'
                              : 'bg-white text-[#0A3A5A] border-[rgba(10,58,90,0.15)] hover:border-red-300 hover:bg-red-50/50'
                          }`}>
                          {name}
                          <span className={`text-[10px] tabular-nums ${active ? 'text-white/80' : 'text-gray-400'}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* ── CANDIDATE GRID ── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-20">
        {loading && rows.length === 0 ? (
          <div className="text-center py-20">
            <div className="h-10 w-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">{tr ? 'Yükleniyor...' : 'Loading...'}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[rgba(10,58,90,0.08)] rounded-3xl p-16 text-center">
            <HardHat className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-sm text-gray-500">
              {search || activeCategory
                ? (tr ? 'Aramayla eşleşen aday bulunamadı.' : 'No matching candidates.')
                : (tr ? 'Henüz aday önerilmemiş. İlk önerinin gelmesi için organizatörlerimizin önericilerle iletişimde olması gerekiyor.' : 'No candidates have been recommended yet.')}
            </p>
            <Link to="/muteahhit" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-red-700 hover:text-red-800">
              <ArrowLeft className="h-4 w-4" /> {tr ? 'Ana Sayfaya Dön' : 'Back to Home'}
            </Link>
          </div>
        ) : view === 'list' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-aday-list">
            {filtered.map(c => (
              <div key={c.id} className="group bg-white border border-[rgba(10,58,90,0.08)] rounded-xl px-4 py-3 hover:border-red-300 hover:shadow-md transition-all duration-200 flex flex-col" data-testid={`public-aday-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-playfair text-base leading-tight truncate" style={{ color: NAVY }} data-testid={`public-aday-company-${c.id}`}>{c.company}</h3>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1 whitespace-nowrap flex-shrink-0">
                    <Calendar className="h-3 w-3" />{(c.created_at || '').slice(0, 10)}
                  </span>
                </div>
                {c.category && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 border border-red-100 mt-1.5 self-start">
                    <Tag className="h-2.5 w-2.5" /> {c.category}
                  </span>
                )}
                <div className="flex items-center gap-1.5 text-[11px] text-gray-600 mt-2 min-w-0">
                  <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">
                    {tr ? 'Öneren: ' : 'By: '}
                    <strong className="text-gray-800" data-testid={`public-aday-recommender-${c.id}`}>{c.recommender_name || '—'}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {c.website && (
                    <a href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-red-700 truncate max-w-[140px]"
                       data-testid={`public-aday-website-${c.id}`}>
                      <Globe className="h-3 w-3 flex-shrink-0" /> {c.website}
                    </a>
                  )}
                  {c.has_presentation && (
                    <PresentationButton candId={c.id} name={c.presentation_name} size={c.presentation_size} tr={tr} onOpen={setPreview} />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setDetail(c)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0A3A5A] hover:bg-[#0c4669] text-white text-xs font-semibold py-2 transition-colors"
                  data-testid={`public-aday-detail-${c.id}`}>
                  <Eye className="h-3.5 w-3.5" /> {tr ? 'Detayları Gör' : 'View Details'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(c => (
              <div key={c.id} className="group bg-white border border-[rgba(10,58,90,0.08)] rounded-2xl p-6 hover:border-red-300 hover:shadow-lg transition-all duration-200" data-testid={`public-aday-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-playfair text-xl leading-tight" style={{ color: NAVY }} data-testid={`public-aday-company-${c.id}`}>{c.company}</h3>
                  {c.has_presentation && (
                    <Sparkles className="h-4 w-4 text-amber-500 flex-shrink-0 mt-1" title={tr ? 'Sunum mevcut' : 'Has presentation'} />
                  )}
                </div>

                {c.category && (
                  <div className="mb-3 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-100">
                      <Tag className="h-3 w-3" /> {c.category}
                    </span>
                    {c.website && (
                      <a href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-50 text-gray-700 hover:text-red-700 hover:bg-red-50 border border-gray-200 truncate max-w-[180px]"
                         data-testid={`public-aday-website-${c.id}`}>
                        <Globe className="h-3 w-3" /> {c.website}
                      </a>
                    )}
                  </div>
                )}
                {!c.category && c.website && (
                  <div className="mb-3">
                    <a href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-50 text-gray-700 hover:text-red-700 hover:bg-red-50 border border-gray-200">
                      <Globe className="h-3 w-3" /> {c.website}
                    </a>
                  </div>
                )}

                {c.note && (
                  <div className="mb-4 flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
                    <MessageSquare className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                    <p className="line-clamp-3 italic">&ldquo;{c.note}&rdquo;</p>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5 text-gray-600 min-w-0">
                    <User className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <span className="truncate">
                      {tr ? 'Öneren: ' : 'Recommended by: '}
                      <strong className="text-gray-800" data-testid={`public-aday-recommender-${c.id}`}>{c.recommender_name || (tr ? '—' : '—')}</strong>
                    </span>
                  </div>
                  <span className="text-gray-400 flex items-center gap-1 whitespace-nowrap">
                    <Calendar className="h-3 w-3" />{(c.created_at || '').slice(0, 10)}
                  </span>
                </div>

                {c.has_presentation && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <PresentationButton candId={c.id} name={c.presentation_name} size={c.presentation_size} tr={tr} onOpen={setPreview} />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setDetail(c)}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#0A3A5A] hover:bg-[#0c4669] text-white text-sm font-semibold py-2.5 transition-colors"
                  data-testid={`public-aday-detail-${c.id}`}>
                  <Eye className="h-4 w-4" /> {tr ? 'Detayları Gör' : 'View Details'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-[rgba(10,58,90,0.08)] py-10 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row gap-3 items-center justify-between text-[12px] text-[#5A6B7D]">
          <p>© 2026 COP 31 Antalya Platform · {tr ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}</p>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-red-700 transition-colors">{tr ? 'Ana Sayfa' : 'Home'}</Link>
            <Link to="/tedarikci" className="hover:text-red-700 transition-colors">{tr ? 'Tedarikçi Programı' : 'Supplier Program'}</Link>
            <Link to="/giris?tab=supplier&only=1" className="hover:text-red-700 transition-colors">{tr ? 'Giriş' : 'Sign in'}</Link>
          </div>
        </div>
      </footer>

      {/* Phase 86AZ.60.2 — Inline presentation preview modal */}
      {preview && (
        <PresentationPreviewModal
          open={!!preview}
          onClose={() => setPreview(null)}
          fileUrl={`${API}/contractor-recommenders/candidates/${preview.candId}/presentation`}
          fileName={preview.name}
        />
      )}

      {/* Phase 86AZ.60.11 — Aday detay modalı (tüm bilgiler + dosya önizleme) */}
      {detail && (
        <DetailModal
          c={detail}
          tr={tr}
          api={API}
          onClose={() => setDetail(null)}
          onFullPreview={() => {
            setDetail(null);
            setPreview({ candId: detail.id, name: detail.presentation_name, size: detail.presentation_size });
          }}
        />
      )}
    </div>
  );
}

// ── Aday Detay Modalı ────────────────────────────────────────────────────────
function DetailModal({ c, tr, api, onClose, onFullPreview }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fileUrl = `${api}/contractor-recommenders/candidates/${c.id}/presentation`;
  const ext = (c.presentation_name || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isPpt = ext === 'ppt' || ext === 'pptx';
  const previewSrc = isPdf ? fileUrl : (isPpt ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}` : null);
  const websiteHref = c.website ? (/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`) : null;

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="public-aday-detail-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#0A3A5A] text-white px-6 py-4 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-1">{tr ? 'Önerilen Tedarikçi' : 'Recommended Supplier'}</p>
            <h2 className="font-playfair text-2xl leading-tight truncate" data-testid="detail-company">{c.company}</h2>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg bg-white/10 hover:bg-red-500 flex items-center justify-center flex-shrink-0 transition-colors" data-testid="detail-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Meta chips */}
          <div className="flex flex-wrap items-center gap-2">
            {c.category && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100">
                <Tag className="h-3.5 w-3.5" /> {c.category}
              </span>
            )}
            {websiteHref && (
              <a href={websiteHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 hover:text-red-700 hover:bg-red-50 border border-gray-200" data-testid="detail-website">
                <Globe className="h-3.5 w-3.5" /> {c.website}
              </a>
            )}
          </div>

          {/* Info rows */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{tr ? 'Öneren' : 'Recommended by'}</p>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-gray-400" />{c.recommender_name || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{tr ? 'Öneri Tarihi' : 'Submitted on'}</p>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gray-400" />{(c.created_at || '').slice(0, 10)}</p>
            </div>
          </div>

          {/* Firma iletişim bilgileri (girilen tüm detaylar) */}
          {(c.contact_name || c.contact_email || c.contact_phone) && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">{tr ? 'Firma İletişim Bilgileri' : 'Firm Contact Details'}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {c.contact_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-700" data-testid="detail-contact-name">
                    <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{c.contact_name}</span>
                  </div>
                )}
                {c.contact_email && (
                  <a href={`mailto:${c.contact_email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-red-700" data-testid="detail-contact-email">
                    <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{c.contact_email}</span>
                  </a>
                )}
                {c.contact_phone && (
                  <a href={`tel:${c.contact_phone}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-red-700" data-testid="detail-contact-phone">
                    <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{c.contact_phone}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Note */}
          {c.note && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />{tr ? 'Öneren Notu' : 'Recommender Note'}</p>
              <p className="text-sm text-gray-700 leading-relaxed italic bg-amber-50 border border-amber-100 rounded-xl p-4">&ldquo;{c.note}&rdquo;</p>
            </div>
          )}

          {/* Presentation preview */}
          {c.has_presentation ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{tr ? 'Firma Sunumu' : 'Company Presentation'}</p>
                <div className="flex items-center gap-2">
                  <button onClick={onFullPreview} className="inline-flex items-center gap-1 text-xs font-medium text-[#0A3A5A] hover:underline" data-testid="detail-fullpreview">
                    <Maximize2 className="h-3.5 w-3.5" /> {tr ? 'Tam Ekran' : 'Full Screen'}
                  </button>
                  <a href={fileUrl} download={c.presentation_name} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:underline" data-testid="detail-download">
                    <Download className="h-3.5 w-3.5" /> {tr ? 'İndir' : 'Download'}
                  </a>
                </div>
              </div>
              {previewSrc ? (
                <iframe
                  key={previewSrc}
                  src={previewSrc}
                  title={c.presentation_name || 'preview'}
                  className="w-full h-[400px] border border-gray-200 rounded-xl bg-white"
                  data-testid="detail-preview-iframe"
                />
              ) : (
                <a href={fileUrl} download={c.presentation_name} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-gray-200 rounded-xl p-4 text-sm text-gray-700 hover:bg-gray-50">
                  <FileText className="h-5 w-5 text-red-600" /> {c.presentation_name}
                </a>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">{tr ? 'Bu firma için sunum yüklenmemiş.' : 'No presentation uploaded for this firm.'}</p>
          )}
        </div>
      </div>
    </div>
  );
}
